/**
 * ClientsCount — the "Clients" figure for one device (device list cell + detail field).
 *
 * Connected NOW only (server: services/client_counts). An offline device shows "—" with a
 * subtle secondary line carrying its last-reported count and age, e.g.
 * "19 when last online · 1d ago" — so the operator keeps the context without the number
 * reading as live state. The rules live in lib/liveStatus (unit-tested); this only lays out.
 */
import { clientsDisplay } from "@/lib/liveStatus";
import { fmtAbsolute } from "@/lib/format";
import type { Device } from "@/types/domain";

export function ClientsCount({
  device,
  className,
}: {
  device: Pick<Device, "online" | "clients_count" | "clients_last_known" | "clients_last_known_at">;
  className?: string;
}) {
  const d = clientsDisplay(device);
  return (
    <span className={`inline-flex flex-col leading-tight ${className ?? ""}`}>
      <span
        className={device.online ? "tabular-nums text-ink" : "text-ink-subtle"}
        title={device.online ? "Clients connected now" : "Device offline: no connected clients"}
      >
        {d.primary}
      </span>
      {d.secondary && (
        <span
          className="text-[0.6875rem] text-ink-subtle"
          title={
            device.clients_last_known_at
              ? `Last state report ${fmtAbsolute(device.clients_last_known_at)}`
              : undefined
          }
        >
          {d.secondary}
        </span>
      )}
    </span>
  );
}
